import { supabase } from "../../lib/supabase";
import { getUserByAuthId } from "./usersService";
import { updateStoreStock } from "./storesService";
import { getFromCache, setToCache } from "./cacheService";

export const getProducts = (callback: (products: any[]) => void) => {
  const fetchProducts = async () => {
    let allProducts: any[] = [];
    let page = 0;
    const pageSize = 1000;
    let hasMore = true;

    while (hasMore) {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *, 
          games:games(id, name), 
          product_types:product_types(id, name, category_id, game_id),
          store_products:store_products(quantity, reserved_quantity, store_price, deleted_at, pre_sale, max_per_user, store:stores(id, name, logo, slug, is_partner, sales_enabled))
        `)
        .order('created_at', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);
      
      if (error) {
        console.error('Error fetching products:', error);
        return;
      }

      if (data && data.length > 0) {
        allProducts = [...allProducts, ...data];
        if (data.length < pageSize) {
          hasMore = false;
        } else {
          page++;
        }
      } else {
        hasMore = false;
      }
    }

    const mappedData = allProducts.map(p => {
      const isTicket = p.product_type_id === 52 || p.product_type_id === 3 || p.product_types?.name === 'Ticket' || p.product_types?.name === 'Ingresso' || p.product_types?.name === 'Inscrição';
      const factor = isTicket ? 1 : 5;
      const originalMsrp = p.msrp || p.mspr || 0;
      const calculatedMsrp = originalMsrp ? Math.round(Number(originalMsrp) * factor * 100) / 100 : originalMsrp;
      return {
        ...p,
        msrp: calculatedMsrp,
        mspr: calculatedMsrp,
        game_name: p.games?.name || (Array.isArray(p.games) ? p.games[0]?.name : 'TCG'),
        product_type_name: p.product_types?.name || (Array.isArray(p.product_types) ? p.product_types[0]?.name : 'Produto')
      };
    });

    callback(mappedData);
  };

  fetchProducts();

  // Set up real-time subscription with a unique channel name to avoid conflicts
  const channelName = `public:products:${Math.random().toString(36).substring(2)}`;
  const subscription = supabase
    .channel(channelName)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'products' }, fetchProducts)
    .subscribe();

  return () => {
    supabase.removeChannel(subscription);
  };
};


export const getMyProductsCount = async (storeId?: number | string) => {
    try {
        let resolvedStoreId = storeId ? Number(storeId) : null;

        if (!resolvedStoreId) {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.user) return 0;

            const profile = await getUserByAuthId(session.user.id);
            if (!profile) return 0;

            if (profile.store_id) {
                resolvedStoreId = Number(profile.store_id);
            } else {
                const { data: store } = await supabase
                  .from('stores')
                  .select('id, store_members!inner(user_id)')
                  .eq('store_members.user_id', profile.id)
                  .maybeSingle();
                resolvedStoreId = store?.id ? Number(store.id) : null;
            }
        }

        if (!resolvedStoreId) return 0;

        const { count, error } = await supabase
          .from('store_products')
          .select('*', { count: 'exact', head: true })
          .eq('store_id', resolvedStoreId)
          .is('deleted_at', null);

        if (error) throw error;
        return count || 0;
    } catch (error) {
        console.error('Error in getMyProductsCount:', error);
        return 0;
    }
};


export const getProductTypes = async (gameId?: string | number) => {
  const cacheKey = `product_types_${gameId || 'all'}`;
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  try {
    let query = supabase.from('product_types').select('*');
    if (gameId && gameId !== 'all') {
      query = query.or(`game_id.eq.${gameId},game_id.is.null`);
    }
    
    const { data, error } = await query.order('name', { ascending: true });
    if (error) throw error;
    setToCache(cacheKey, data || []);
    return data || [];
  } catch (error) {
    console.error('Error fetching product types:', error);
    return [];
  }
};


export const getProductsByFilters = async (filters: { game_id?: string | number, product_type_id?: string | number }) => {
  try {
    let query = supabase.from('products').select(`
      *, 
      games:games(name), 
      product_types:product_types(name)
    `);
    if (filters.game_id && filters.game_id !== 'all') query = query.eq('game_id', filters.game_id);
    if (filters.product_type_id && filters.product_type_id !== 'all') query = query.eq('product_type_id', filters.product_type_id);
    
    const { data, error } = await query.order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map((p: any) => {
      const isTicket = p.product_type_id === 52 || p.product_type_id === 3 || p.product_types?.name === 'Ticket' || p.product_types?.name === 'Ingresso' || p.product_types?.name === 'Inscrição';
      const factor = isTicket ? 1 : 5;
      const originalMsrp = p.msrp || p.mspr || 0;
      const calculatedMsrp = originalMsrp ? Math.round(Number(originalMsrp) * factor * 100) / 100 : originalMsrp;
      return {
        ...p,
        msrp: calculatedMsrp,
        mspr: calculatedMsrp,
      };
    });
  } catch (error) {
    console.error('Error fetching filtered products:', error);
    return [];
  }
};


export const getProductCategories = async () => {
  const cacheKey = 'product_categories';
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  try {
    const { data, error } = await supabase
      .from('product_categories')
      .select('*')
      .order('name', { ascending: true });
    if (error) throw error;
    setToCache(cacheKey, data || []);
    return data || [];
  } catch (error) {
    console.error('Error fetching product categories:', error);
    return [];
  }
};


export const createCustomProduct = async (storeId: number | string, productData: {
  name: string;
  description: string;
  image_url: string;
  game_id: number | null;
  product_type_id: number;
  mspr: number;
  quantity: number;
}) => {
  try {
    const finalPrice = Math.max(0.05, Number(productData.mspr) || 0.05);

    const slugBase = productData.name.toLowerCase()
      .normalize('NFD') // remove accents
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9 ]/g, '')
      .replace(/\s+/g, '-');
    const slug = `${slugBase}-${Date.now()}`;

    const { data: product, error: pError } = await supabase
      .from('products')
      .insert({
        name: productData.name,
        slug: slug,
        description: productData.description || '',
        image_url: productData.image_url || 'https://images.unsplash.com/photo-1614850523296-d8c1af93d400?auto=format&fit=crop&q=80&w=400',
        game_id: productData.game_id || null,
        product_type_id: Number(productData.product_type_id),
        msrp: finalPrice,
        store_id: Number(storeId),
        is_official: false
      })
      .select()
      .maybeSingle();

    if (pError) throw pError;
    if (!product) throw new Error("Não foi possível criar o produto customizado.");

    // Update store stock for this product
    const stockRes = await updateStoreStock(Number(storeId), product.id, productData.quantity, finalPrice, false);
    if (!stockRes || (stockRes as any).error) {
      throw new Error((stockRes as any).error || "Erro ao adicionar produto customizado ao estoque.");
    }

    return { success: true, product };
  } catch (error: any) {
    console.error('Error creating custom product:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Obtém os IDs de produtos salvos na lista de desejos do usuário
 */
export const getUserProductWishlistIds = async (userId: string | number): Promise<number[]> => {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from('user_product_wishlist')
      .select('product_id')
      .eq('user_id', Number(userId));

    if (error) {
      console.error('Error fetching user_product_wishlist IDs:', error);
      return [];
    }

    return (data || []).map((row: any) => Number(row.product_id));
  } catch (error) {
    console.error('Unexpected error fetching user_product_wishlist IDs:', error);
    return [];
  }
};

/**
 * Alterna um produto na lista de desejos do usuário (adiciona se não tiver, remove se já tiver)
 */
export const toggleUserProductWishlist = async (
  userId: string | number,
  productId: string | number
): Promise<{ inWishlist: boolean; error?: string }> => {
  if (!userId || !productId) {
    return { inWishlist: false, error: 'Usuário ou produto inválido.' };
  }

  const uId = Number(userId);
  const pId = Number(productId);

  try {
    // Verifica se já está na lista
    const { data: existing, error: checkError } = await supabase
      .from('user_product_wishlist')
      .select('id')
      .eq('user_id', uId)
      .eq('product_id', pId)
      .maybeSingle();

    if (checkError) {
      console.error('Error checking user_product_wishlist:', checkError);
      return { inWishlist: false, error: checkError.message };
    }

    if (existing) {
      // Se já existe, remove
      const { error: deleteError } = await supabase
        .from('user_product_wishlist')
        .delete()
        .eq('id', existing.id);

      if (deleteError) {
        console.error('Error removing from user_product_wishlist:', deleteError);
        return { inWishlist: true, error: deleteError.message };
      }

      return { inWishlist: false };
    } else {
      // Se não existe, adiciona
      const { error: insertError } = await supabase
        .from('user_product_wishlist')
        .insert({
          user_id: uId,
          product_id: pId
        });

      if (insertError) {
        console.error('Error inserting into user_product_wishlist:', insertError);
        return { inWishlist: false, error: insertError.message };
      }

      return { inWishlist: true };
    }
  } catch (error: any) {
    console.error('Unexpected error toggling user_product_wishlist:', error);
    return { inWishlist: false, error: error?.message || 'Erro inesperado.' };
  }
};

/**
 * Obtém todos os produtos detalhados que o usuário favoritou na lista de desejos
 */
export const getUserProductWishlist = async (userId: string | number): Promise<any[]> => {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from('user_product_wishlist')
      .select(`
        id,
        created_at,
        product:products(
          *,
          games:games(id, name),
          product_types:product_types(id, name, category_id, game_id),
          store_products:store_products(quantity, reserved_quantity, store_price, deleted_at, store:stores(id, name, logo, slug, is_partner, sales_enabled))
        )
      `)
      .eq('user_id', Number(userId))
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching full user_product_wishlist:', error);
      return [];
    }

    return (data || []).map((row: any) => row.product).filter(Boolean);
  } catch (error) {
    console.error('Unexpected error fetching full user_product_wishlist:', error);
    return [];
  }
};
